import { useMemo, useState } from 'react'
import { SingleSelect } from './SingleSelect'
import { parseCrossFormRef } from '@/lib/crossForm'
import { useOperandSuggestions } from './operandSuggestions'

/** One form whose fields can be referenced: the current form or an upstream one. */
export interface RefFormGroup {
  /** '' for the current form; the form's permanent handle for upstream forms. */
  key: string
  /** Display name — "This form — Processing" / "Patient Chart". */
  name: string
  /** Fields, with the full ref key each resolves to (own id / handle.fieldId). */
  fields: Array<{ refKey: string; label: string; type?: string }>
}

interface Props {
  groups: RefFormGroup[]
  /**
   * The selected ref key ('' = none). Persistent-value mode: the form select is
   * derived from it. Insert mode (value always '') keeps the chosen form sticky
   * and resets the field select after each onChange, so several fields can be
   * inserted in a row.
   */
  value: string
  onChange: (refKey: string) => void
  /** Fires when the form select changes ('' = the current form). */
  onFormChange?: (formKey: string) => void
  formPlaceholder?: string
  fieldPlaceholder?: string
  /** Compact 8-height row (condition rows); default is the 10-height panel size. */
  size?: 'default' | 'sm'
  /**
   * Pin "Suggested" fields above the field list (z8nrz7deww), from what saved
   * formulas pair with the operands of `formula`. `owner` is the field whose
   * formula it is, so its own saved copy is left out. Absent: no suggestions.
   */
  suggestFor?: { formula: string; owner?: string }
}

const NO_GROUPS: RefFormGroup[] = []

/** Which group a stored ref key belongs to ('' = current form). */
function groupKeyOf(refKey: string, groups: RefFormGroup[]): string {
  if (!refKey) return ''
  const cross = parseCrossFormRef(refKey)
  if (cross && groups.some(g => g.key === cross.handle)) return cross.handle
  return ''
}

/**
 * Hierarchical variable picker: first a searchable FORM select (the current
 * form always included, upstream forms per the toolbar template context), then
 * a searchable FIELD select scoped to the chosen form. Emits the full ref key
 * — a plain field id for the current form, `handle.fieldId` for upstream ones.
 */
export function FormFieldRefSelect({
  groups,
  value,
  onChange,
  onFormChange,
  formPlaceholder = 'Pick a form…',
  fieldPlaceholder = 'Pick a field…',
  size = 'default',
  suggestFor,
}: Props) {
  const suggestions = useOperandSuggestions(suggestFor ? groups : NO_GROUPS, suggestFor?.formula ?? '', suggestFor?.owner)
  // Sticky manual choice; falls back to the group the current value lives in,
  // then to the current form.
  const [pickedForm, setPickedForm] = useState<string | null>(null)
  const derivedForm = value ? groupKeyOf(value, groups) : ''
  const formKey =
    pickedForm !== null && groups.some(g => g.key === pickedForm) ? pickedForm : derivedForm
  const group = groups.find(g => g.key === formKey) ?? groups[0]

  const formOptions = useMemo(
    () => groups.map(g => ({ id: g.key || '__self', label: g.name })),
    [groups],
  )
  const fieldOptions = useMemo(
    () =>
      (group?.fields ?? []).map(f => ({
        id: f.refKey,
        label: f.type ? `${f.label} (${f.type})` : f.label,
      })),
    [group],
  )

  return (
    <div className='flex min-w-0 flex-1 flex-col gap-1'>
      <SingleSelect
        size={size}
        options={formOptions}
        value={formKey || '__self'}
        onChange={id => {
          const key = id === '__self' || id === '' ? '' : id
          setPickedForm(key)
          onFormChange?.(key)
        }}
        placeholder={formPlaceholder}
        searchPlaceholder='Search forms…'
        searchThreshold={0}
        ariaLabel='Source form'
      />
      <SingleSelect
        size={size}
        options={fieldOptions}
        value={value}
        onChange={refKey => refKey && onChange(refKey)}
        placeholder={fieldPlaceholder}
        searchPlaceholder='Search fields…'
        searchThreshold={0}
        ariaLabel='Source field'
        pinned={
          suggestions?.length
            ? { label: 'Suggested', options: suggestions.map(sg => ({ id: sg.refKey, label: sg.label, hint: sg.reason })) }
            : undefined
        }
      />
    </div>
  )
}
